import React from "react";
import { useTranslation } from "react-i18next";
import {
  alpha,
  Chip,
  Collapse,
  Divider,
  InputBase,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { SearchIcon2 } from "../../components/Icon";
import Icon from "../../components/Icon/Icon";
import { EGButton } from "../../components/MainComponents/Button/EGButton/EGButton";
import { EGIconButton } from "../../components/MainComponents/Button/IconButton/IconButton";
import { SEARCH_DOMAINS } from "./searchDomains";
import {
  countActiveFilters,
  SearchOptionsPanel,
  SearchOptionsToggle,
} from "./SearchOptions";
import { useFilterOptions } from "./useFilterOptions";

/**
 * Tarjeta de accesos rápidos a "Buscar en": asoma por debajo del input (queda
 * metida tras la píldora, que la tapa con su zIndex) con un chip por campo
 * consultable, para cambiar de campo sin abrir el panel de opciones.
 *
 * Solo lista los campos habilitados: los cifrados ya se explican con su
 * candado dentro del panel y aquí solo estorbarían. Se desmonta cuando el
 * panel está abierto porque ahí vive el grupo completo de "Buscar en".
 */
const FieldShortcuts = ({ domain, fields, onToggleField }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();

  const enabledFields = domain.fields.filter((field) => field.enabled);
  // Con un solo campo posible no hay nada que atajar
  if (enabledFields.length < 2) return null;

  return (
    // El Collapse arranca en 0 y crece hacia abajo: con el margen negativo la
    // tarjeta literalmente sale desde atrás del input al montarse.
    <Collapse in appear timeout={220} sx={{ width: "100%", mt: "-20px" }}>
      <Stack
        direction="row"
        sx={{
          // Un poco mas angosta que el input: refuerza que sale de abajo de el
          width: "calc(100% - 36px)",
          mx: "auto",
          // 20px quedan ocultos tras la píldora del input
          pt: "28px",
          pb: 1,
          px: 2,
          gap: 0.75,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "0 0 18px 18px",
          border: `1px solid ${theme.palette.blueGray200.main}`,
          borderTop: "none",
          bgcolor: theme.palette.background.paper,
          boxShadow: `0 6px 12px -8px ${alpha(theme.palette.common.black, 0.18)}`,
        }}
      >
        <Typography
          variant="body2"
          fontSize={11}
          color="text.secondary"
          sx={{ mr: 0.25, whiteSpace: "nowrap" }}
        >
          {t("searchBar.where_to_search_short")}:
        </Typography>

        {enabledFields.map((field) => {
          const active = fields.includes(field.key);

          return (
            <Tooltip
              key={field.key}
              title={(field.hintKey && t(field.hintKey)) || ""}
              placement="bottom"
              disableInteractive
              enterDelay={400}
            >
              <Chip
                size="small"
                clickable
                aria-pressed={active}
                label={t(field.labelKey)}
                onClick={() => onToggleField(field.key)}
                sx={{
                  height: 24,
                  fontSize: 12,
                  fontWeight: active ? 600 : 400,
                  color: active
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  bgcolor: active
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                  border: `1px solid ${
                    active
                      ? alpha(theme.palette.primary.main, 0.35)
                      : theme.palette.blueGray200.main
                  }`,
                  transition:
                    "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                  "&:hover, &.MuiChip-clickable:hover": {
                    bgcolor: active
                      ? alpha(theme.palette.primary.main, 0.16)
                      : alpha(theme.palette.blueGray100.main, 0.9),
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Collapse>
  );
};

/**
 * Selector de entidad + input + opciones.
 *
 * La entidad se elige primero y de ella depende todo lo demás: dónde se busca
 * el término y por qué se puede acotar viven en un solo select (SearchOptions),
 * y lo que quede activo se muestra como chips removibles debajo.
 *
 * @param variant "hero" (sin búsqueda todavía) | "compact" (con resultados)
 */
export const SearchField = ({
  domain,
  onChangeDomain,
  value,
  onChange,
  onSubmit,
  onClear,
  fields,
  onToggleField,
  advanced,
  onToggleFilterValue,
  onSetFilterValue,
  onClearAll,
  openOptions,
  onToggleOptions,
  isLoadingCatalogues,
  autoFocus = false,
  variant = "hero",
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const optionsCatalog = useFilterOptions();
  const isHero = variant === "hero";

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit();
    }
  };

  // Un filtro estático nombra su catálogo; uno de catálogo trae sus opciones ya
  // resueltas desde el formulario.
  const resolveOptions = (filter) =>
    Array.isArray(filter.options)
      ? filter.options
      : optionsCatalog[filter.options] || [];

  const hasActiveChips = domain.filters.some((filter) => {
    const selected = advanced[filter.key];
    return Array.isArray(selected) ? selected.length > 0 : Boolean(selected);
  });

  const fieldLabels = fields
    .map((key) => domain.fields.find((field) => field.key === key))
    .filter(Boolean)
    .map((field) => t(field.labelKey));

  return (
    <Stack sx={{ width: "100%", alignItems: isHero ? "center" : "flex-start" }}>
      {/* Paso 1: entidad */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          mb: 1.5,
          p: 0.5,
          borderRadius: 6,
          bgcolor: alpha(theme.palette.blueGray100.main, 0.7),
        }}
      >
        {Object.values(SEARCH_DOMAINS).map((item) => (
          <EGButton
            key={item.key}
            label={t(item.labelKey)}
            color={item.key === domain.key ? "primary" : "light"}
            variant={item.key === domain.key ? "contained" : "text"}
            onClick={() => onChangeDomain(item.key)}
            sx={{ px: 2, py: 0.25, borderRadius: 6 }}
          />
        ))}
      </Stack>

      {/* Paso 2: término y opciones */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: isHero ? 52 : 44,
          px: 1.5,
          borderRadius: 6,
          // Encima de la tarjeta de shortcuts, que asoma por debajo
          position: "relative",
          zIndex: 1,
          border: `1px solid ${theme.palette.blueGray200.main}`,
          boxShadow: `0 1px 6px ${alpha(theme.palette.common.black, 0.12)}`,
          transition: "box-shadow 0.2s ease-in-out",
          "&:hover, &:focus-within": {
            boxShadow: `0 1px 10px ${alpha(theme.palette.common.black, 0.22)}`,
          },
        }}
      >
        <SearchIcon2
          size={isHero ? 20 : 18}
          color={theme.palette.blueGray500.main}
        />
        <InputBase
          autoFocus={autoFocus}
          sx={{ ml: 1.5, flex: 1, fontSize: isHero ? 17 : 15 }}
          placeholder={t("searchBar.search_by", {
            fields: fieldLabels.join(" · "),
          })}
          inputProps={{ "aria-label": t("searchBar.search_in_casemanager") }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {value.length > 0 && (
          <EGIconButton
            iconName="close_icon"
            title={t("searchBar.clear_search")}
            iconColorHover="black"
            onClick={onClear}
          />
        )}

        <Divider sx={{ height: 22, mx: 1 }} orientation="vertical" />

        <SearchOptionsToggle
          open={openOptions}
          onToggle={onToggleOptions}
          activeCount={countActiveFilters(advanced)}
        />

        <EGIconButton
          iconName="search2"
          iconSize="20"
          iconColorHover="black"
          title={t("searchBar.search")}
          placement="bottom"
          onClick={onSubmit}
        />
      </Paper>

      {/* Shortcuts de "Buscar en" pegados al input; el panel abierto ya trae
          el grupo completo, así que no se muestran a la vez */}
      {!openOptions && (
        <FieldShortcuts
          domain={domain}
          fields={fields}
          onToggleField={onToggleField}
        />
      )}

      {/* Panel de opciones a todo lo ancho, bajo el input */}
      <Collapse in={openOptions} sx={{ width: "100%" }} unmountOnExit>
        <SearchOptionsPanel
          domain={domain}
          fields={fields}
          onToggleField={onToggleField}
          advanced={advanced}
          onToggleFilterValue={onToggleFilterValue}
          onSetFilterValue={onSetFilterValue}
          onClearAll={onClearAll}
          isLoadingCatalogues={isLoadingCatalogues}
        />
      </Collapse>

      {/* Filtros activos */}
      <Stack
        sx={{
          mt: hasActiveChips ? 1.25 : 0,
          gap: 0.75,
          flexFlow: "row wrap",
          alignItems: "center",
          justifyContent: isHero ? "center" : "flex-start",
        }}
      >
        {domain.filters.flatMap((filter) => {
          const options = resolveOptions(filter);
          const selected = advanced[filter.key];
          const values = Array.isArray(selected)
            ? selected
            : selected
            ? [selected]
            : [];

          return values.map((value) => {
            const option = options.find((item) => item.value === value);

            return (
              <Chip
                key={`${filter.key}-${value}`}
                size="small"
                label={option?.label || value}
                onDelete={() =>
                  filter.multiple
                    ? onToggleFilterValue(filter.key, value)
                    : onSetFilterValue(filter.key, "")
                }
                icon={
                  option?.iconName ? (
                    <Icon
                      name={option.iconName}
                      size={14}
                      color={theme.palette.primary.main}
                    />
                  ) : undefined
                }
                sx={{
                  fontSize: 11,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              />
            );
          });
        })}
      </Stack>
    </Stack>
  );
};
